<template>
  <a-layout class="admin-shell" :class="{ 'admin-mobile': isMobile }" :lang="$adminLocale" style="min-height: 100vh">
    <div v-if="isMobile && mobileNavOpen" class="mobile-navigation-backdrop" @click="closeNavigation()" aria-hidden="true"></div>
    <a-layout-sider v-show="!isMobile || mobileNavOpen" id="admin-navigation" ref="navigation" :collapsed="!isMobile && collapsed" :class="{ 'mobile-navigation': isMobile }" :trigger="null" :width="isMobile ? 280 : 220" :role="isMobile ? 'dialog' : 'navigation'" :aria-modal="isMobile && mobileNavOpen ? 'true' : null" :aria-label="mobileText('navigation')" @keydown.native="onNavigationKeydown">
      <button v-if="isMobile" type="button" class="mobile-navigation-close" :aria-label="mobileText('closeNavigation')" @click="closeNavigation()"><a-icon type="close" /></button>
      <div class="brand" @click="goHome">
        <div class="brand-logo"><img src="/logo.png" alt="桨刻" /></div>
        <transition name="fade">
          <div v-if="isMobile || !collapsed" class="brand-text">
            <div class="brand-name">{{ $t('brandName') }}</div>
            <div class="brand-sub">JIANGK SPORTS</div>
          </div>
        </transition>
      </div>
      <a-menu theme="dark" :selected-keys="selectedMenuKeys" mode="inline" :inline-collapsed="!isMobile && collapsed">
        <template v-for="item in routes">
          <template v-if="!item.hidden">

            <a-menu-item 
              v-if="isSingleItem(item)" 
              :key="resolveKey(item)"
              @click="handleSingleClick(item)"
            >
              <a-icon :type="getIcon(item)" />
              <span>{{ getTitle(item) }}</span>
            </a-menu-item>

            <a-sub-menu 
              v-else 
              :key="item.path"
            >
              <span slot="title">
                <a-icon :type="item.meta && item.meta.icon ? item.meta.icon : 'appstore'" />
                <span>{{ item.meta && item.meta.title ? $translateRouteTitle(item.meta.title) : $t('unnamed') }}</span>
              </span>
              <template v-for="child in item.children">
                <a-menu-item v-if="!child.hidden" :key="resolvePath(item.path, child.path)"
                  @click="handleMenuClick(item.path, child.path)">
                  <span>{{ $translateRouteTitle(child.meta && child.meta.title ? child.meta.title : child.name) }}</span>
                </a-menu-item>
              </template>
            </a-sub-menu>

          </template>
        </template>
      </a-menu>
    </a-layout-sider>

    <a-layout class="admin-main" :inert="isMobile && mobileNavOpen ? '' : null" :aria-hidden="isMobile && mobileNavOpen ? 'true' : null">
      <a-layout-header class="topbar">
        <div class="topbar-left">
          <button ref="navigationTrigger" class="trigger" type="button" :aria-label="mobileText((isMobile ? mobileNavOpen : !collapsed) ? 'closeNavigation' : 'openNavigation')" :aria-expanded="isMobile ? mobileNavOpen : !collapsed" aria-controls="admin-navigation" @click="toggleNavigation">
            <a-icon :type="isMobile || collapsed ? 'menu-unfold' : 'menu-fold'" />
          </button>
          <span class="page-title">{{ pageTitle }}</span>
        </div>
        <div class="topbar-right">
        <div class="admin-language-switch" :aria-label="$t('language')">
          <button
            v-for="item in languageOptions"
            :key="item.value"
            type="button"
            :class="{ active: $adminLocalePreference === item.value }"
            :aria-pressed="$adminLocalePreference === item.value"
            @click="$setAdminLocale(item.value)"
          >{{ item.short }}</button>
        </div>
        <a-dropdown placement="bottomRight" :trigger="['click']">
          <button type="button" class="theme-trigger" :aria-label="$t('appearance')" :title="themeLabel">
            <a-icon :type="themeIcon" />
            <span class="theme-label">{{ themeLabel }}</span>
            <a-icon type="down" class="theme-chevron" />
          </button>
          <a-menu slot="overlay" class="theme-menu" :selected-keys="[$adminThemePreference]" @click="changeTheme">
            <a-menu-item v-for="item in themeOptions" :key="item.value">
              <a-icon :type="item.icon" /> {{ $t(item.label) }}
            </a-menu-item>
          </a-menu>
        </a-dropdown>
        <a-dropdown placement="bottomRight" :trigger="['click']" :visible="noticeVisible" @visibleChange="onNoticeVisibleChange">
          <a-badge :count="noticeTotal" :overflow-count="99" :offset="[-4, 4]">
            <button type="button" class="notice-trigger" :aria-label="$t('todoAndNotice')">
              <a-icon type="bell" style="font-size:18px; color:#2C3A4B;" />
            </button>
          </a-badge>
          <div slot="overlay" class="notice-panel">
            <div class="notice-head">
              <span>{{ $t('todoAndNotice') }}</span>
              <a style="font-size:12px;" @click="loadNotice">{{ $t('refresh') }}</a>
            </div>
            <div class="notice-body">
              <div class="notice-item" v-if="notice.authPending > 0" @click="goNotice('/dragonboat/auth/verifications')">
                <span class="dot orange"></span>
                <span class="notice-text">{{ $t('authPending', { count: notice.authPending }) }}</span>
                <span class="arrow">›</span>
              </div>
              <div class="notice-item" v-if="notice.cancelPending > 0" @click="goNotice('/dragonboat/auth/cancel-review')">
                <span class="dot red"></span>
                <span class="notice-text">{{ $t('cancelPending', { count: notice.cancelPending }) }}</span>
                <span class="arrow">›</span>
              </div>
              <div class="notice-item" v-if="notice.pendingRegistration > 0" @click="goNotice('/dragonboat/registration')">
                <span class="dot blue"></span>
                <span class="notice-text">{{ $t('registrationPending', { count: notice.pendingRegistration }) }}</span>
                <span class="arrow">›</span>
              </div>
              <div class="notice-item" v-if="notice.pendingTeam > 0" @click="goNotice('/dragonboat/team')">
                <span class="dot purple"></span>
                <span class="notice-text">{{ $t('teamPending', { count: notice.pendingTeam }) }}</span>
                <span class="arrow">›</span>
              </div>
              <div class="notice-item" v-if="notice.total === 0" style="cursor:default; color:#999;">{{ $t('nothingPending') }}</div>
            </div>
            <div class="notice-head" style="border-top:1px solid #f0f0f0;">
              <span>{{ $t('newEvents') }}</span>
            </div>
            <div class="notice-body">
              <div class="notice-item" v-for="ev in notice.events" :key="ev.id" @click="goNotice(`/dragonboat/event/workbench?eventId=${ev.id}`)">
                <span class="dot green"></span>
                <span class="notice-text">{{ ev.title }}<span class="ev-time" v-if="ev.eventTime"> · {{ ev.eventTime }}</span></span>
                <span class="arrow">›</span>
              </div>
              <div class="notice-item" v-if="!notice.events || !notice.events.length" style="cursor:default; color:#999;">{{ $t('noEvents') }}</div>
            </div>
          </div>
        </a-dropdown>
        <a-dropdown :trigger="['click']">
          <button type="button" class="user-box" :aria-label="$t('administrator')">
            <a-avatar size="small" style="background:#4ECDC4;">管</a-avatar>
            <span class="user-name">{{ $t('administrator') }}</span>
            <a-icon type="down" style="font-size:12px; color:#7A8A9A;" />
          </button>
          <a-menu slot="overlay">
            <a-menu-item @click="$setAdminLocale('system')">
              <a-icon type="global" /> {{ $t('language') }} · {{ $t('followSystem') }}
              <a-icon v-if="$adminLocalePreference === 'system'" type="check" />
            </a-menu-item>
            <a-menu-item @click="handleLogout">
              <a-icon type="logout" /> {{ $t('logout') }}
            </a-menu-item>
          </a-menu>
        </a-dropdown>
        </div>
      </a-layout-header>

      <a-layout-content class="page-container">
        <header class="workspace-heading"><h1>{{ pageTitle }}</h1></header>
        <router-view :key="workspaceRouteKey" />
      </a-layout-content>

      <a-layout-footer class="site-footer">
        <span>{{ $t('platformFooter') }}</span>
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
        >粤ICP备2023117792号-3</a>
      </a-layout-footer>
    </a-layout>
    
  </a-layout>
</template>

<script>
  import { mobileUiText } from '@/utils/admin-mobile'
  import { adminRouteKey } from '@/utils/admin-routing'
  import { adminThemeState, resolvedAdminTheme, setAdminTheme, startAdminTheme, stopAdminTheme } from '@/utils/admin-theme'

  export default {
    data() {
      return {
        collapsed: false,
        isMobile: false,
        mobileNavOpen: false,
        noticeVisible: false,
        languageOptions: [
          { value: 'zh-Hans', short: '简' },
          { value: 'zh-Hant', short: '繁' },
          { value: 'en', short: 'EN' }
        ],
        themeOptions: [
          { value: 'system', label: 'followSystem', icon: 'desktop' },
          { value: 'light', label: 'lightMode', icon: 'bulb' },
          { value: 'dark', label: 'darkMode', icon: 'eye' }
        ],
        notice: { authPending: 0, cancelPending: 0, pendingRegistration: 0, pendingTeam: 0, total: 0, events: [] }
      }
    },
    mounted() {
      document.body.classList.add('jiangk-admin')
      startAdminTheme()
      this.mobileMedia = window.matchMedia('(max-width: 767px)')
      this.onMobileBreakpoint(this.mobileMedia)
      if (this.mobileMedia.addEventListener) this.mobileMedia.addEventListener('change', this.onMobileBreakpoint)
      else this.mobileMedia.addListener(this.onMobileBreakpoint)
      this.loadNotice()
    },
    beforeDestroy() {
      document.body.classList.remove('jiangk-admin', 'jiangk-navigation-open')
      stopAdminTheme()
      if (this.mobileMedia) {
        if (this.mobileMedia.removeEventListener) this.mobileMedia.removeEventListener('change', this.onMobileBreakpoint)
        else this.mobileMedia.removeListener(this.onMobileBreakpoint)
      }
    },
    watch: {
      '$route.fullPath'() { this.closeNavigation(false) },
      mobileNavOpen(value) { document.body.classList.toggle('jiangk-navigation-open', this.isMobile && value) }
    },
    computed: {
      workspaceRouteKey() { return adminRouteKey(this.$route) },
      $adminThemePreference() { return adminThemeState.preference },
      themeLabel() {
        const option = this.themeOptions.find(item => item.value === this.$adminThemePreference)
        return this.$t(option ? option.label : 'followSystem')
      },
      themeIcon() {
        if (this.$adminThemePreference === 'system') return 'desktop'
        return resolvedAdminTheme() === 'dark' ? 'eye' : 'bulb'
      },
      selectedMenuKeys() {
        const path = this.$route.path.replace(/\/+$/, '') || '/'
        return [path === '/dragonboat/event/workbench' ? '/dragonboat/event' : path]
      },
      routes() {
        return this.$store.state.routes.map(route => {
          if (route.path !== '/system' || route.hidden) return route
          const children = [...(route.children || [])]
          if (!children.some(item => ['languages', '/system/languages'].includes(item.path))) {
            children.push({ path: 'languages', meta: { title: '语言管理' } })
          }
          return { ...route, children }
        })
      },
      pageTitle() {
        const route = this.$route
        return route.meta && route.meta.title ? this.$translateRouteTitle(route.meta.title) : this.$t('adminBackend')
      },
      noticeTotal() {
        return Number(this.notice.total) || 0
      }
    },
    methods: {
      changeTheme({ key }) { setAdminTheme(key) },
      mobileText(key) { return mobileUiText(this.$adminLocale, key) },
      onMobileBreakpoint(event) { this.isMobile = event.matches; this.closeNavigation(false) },
      toggleNavigation() {
        if (!this.isMobile) { this.collapsed = !this.collapsed; return }
        this.mobileNavOpen = !this.mobileNavOpen
        this.noticeVisible = false
        if (this.mobileNavOpen) this.$nextTick(() => { this.$refs.navigation.$el.querySelector('.mobile-navigation-close').focus() })
      },
      closeNavigation(restoreFocus = true) {
        const wasOpen = this.mobileNavOpen
        this.mobileNavOpen = false
        if (wasOpen && restoreFocus) this.$nextTick(() => this.$refs.navigationTrigger && this.$refs.navigationTrigger.focus())
      },
      onNavigationKeydown(event) {
        if (!this.isMobile || !this.mobileNavOpen) return
        if (event.key === 'Escape') { event.preventDefault(); this.closeNavigation(); return }
        if (event.key !== 'Tab') return
        const targets = [...this.$refs.navigation.$el.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')].filter(el => el.getClientRects().length)
        const first = targets[0], last = targets[targets.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      },
      loadNotice() {
        import('@/api').then(({ getDashboardTodo }) => getDashboardTodo().then(res => {
          this.notice = Object.assign({ authPending: 0, cancelPending: 0, pendingRegistration: 0, pendingTeam: 0, total: 0, events: [] }, res.data || {})
        }).catch(() => {}))
      },
      onNoticeVisibleChange(visible) {
        this.noticeVisible = visible
        if (visible) this.loadNotice()
      },
      goNotice(path) {
        this.noticeVisible = false
        this.$router.push(path)
      },
      isSingleItem(item) {
        // 1. 如果没有 children -> 单菜单
        if (!item.children || item.children.length === 0) {
          return true;
        }
        // 2. 如果是根路径 "/" 且只有一个子节点 -> 也是单菜单 (Dashboard情况)
        if (item.path === '/' && item.children.length === 1) {
          return true;
        }
        // 3. 顶层页面菜单（后端提升的顶级菜单）：children 都是空路径包装 -> 单菜单
        if (item.children.every(c => !c.path)) {
          return true;
        }
        return false;
      },

      getTitle(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return this.$translateRouteTitle(item.children[0].meta?.title || item.children[0].title);
        }
        return this.$translateRouteTitle(item.meta?.title || item.title);
      },

      getIcon(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return item.children[0].meta?.icon || item.children[0].icon || 'desktop';
        }
        return item.meta?.icon || item.icon || 'desktop';
      },

      resolveKey(item) {
        if (item.path === '/' && item.children && item.children.length === 1) {
          return this.resolvePath(item.path, item.children[0].path);
        }
        return item.path;
      },

      handleSingleClick(item) {
        this.closeNavigation(false)
        let targetPath = item.path;
        if (item.path === '/' && item.children && item.children.length === 1) {
          targetPath = this.resolvePath(item.path, item.children[0].path);
        }
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath);
        }
      },

      resolvePath(basePath, routePath) {
        if (!routePath) return basePath;
        if (basePath.endsWith('/')) return basePath + routePath;
        return basePath + '/' + routePath;
      },

      handleMenuClick(basePath, routePath) {
        this.closeNavigation(false)
        const fullPath = this.resolvePath(basePath, routePath);
        if (this.$route.path !== fullPath) {
          this.$router.push(fullPath);
        }
      },

      handleLogout() {
        this.$store.dispatch('Logout');
      },
      goHome() {
        this.closeNavigation(false)
        if (this.$route.path !== '/') {
          this.$router.push('/')
        }
      }
    }
  }
</script>

<style scoped>
.brand {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 18px;
  background: #131F2A;
  cursor: pointer;
  overflow: hidden;
}

.brand-logo {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(78, 205, 196, 0.4);
}

.brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  margin-left: 12px;
  line-height: 1.2;
  white-space: nowrap;
}

.brand-name {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 2px;
}

.brand-sub {
  background: linear-gradient(90deg, #4ECDC4, #B8FFF6, #4ECDC4, #2FBFB5, #4ECDC4);
  background-size: 220% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-size: 10px;
  letter-spacing: 2.5px;
  font-weight: 600;
  animation: brandGlow 4s linear infinite;
  filter: drop-shadow(0 0 6px rgba(78, 205, 196, 0.65));
}

@keyframes brandGlow {
  0% { background-position: 0% center; }
  100% { background-position: 220% center; }
}

.brand-logo {
  box-shadow: 0 4px 10px rgba(78, 205, 196, 0.4), 0 0 18px rgba(78, 205, 196, 0.35);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.trigger {
  font-size: 18px;
  cursor: pointer;
  color: #7A8A9A;
}

.trigger:hover {
  color: #4ECDC4;
}

.page-title {
  color: #2C3A4B;
  font-size: 15px;
  font-weight: 600;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.admin-language-switch {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid #DDE7EC;
  border-radius: 10px;
  background: #F5F8FA;
  line-height: 1;
}

.admin-language-switch button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 auto;
  min-width: 36px;
  height: 28px;
  margin: 0;
  padding: 0 9px;
  border: 0;
  border-radius: 7px;
  color: #6B7B88;
  background: transparent;
  cursor: pointer;
  appearance: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  vertical-align: middle;
  transition: color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.admin-language-switch button:hover {
  color: #2D635F;
  background: #E9F4F3;
}

.admin-language-switch button:focus-visible {
  outline: 2px solid rgba(78, 205, 196, 0.45);
  outline-offset: 1px;
}

.admin-language-switch button.active {
  color: #123C3A;
  background: #AEECE6;
  box-shadow: 0 1px 4px rgba(47, 132, 125, 0.18);
}

.notice-trigger {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #F4FAFA;
  border: 1px solid #E3EEF2;
  transition: all 0.2s;
}

.notice-trigger:hover {
  background: #E7F7F5;
  border-color: #4ECDC4;
  box-shadow: 0 0 12px rgba(78, 205, 196, 0.35);
}

.notice-panel {
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.14);
  overflow: hidden;
  border: 1px solid #E9F0F5;
}

.notice-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #2C3A4B;
  background: #FAFCFE;
}

.notice-body {
  max-height: 260px;
  overflow-y: auto;
}

.notice-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  cursor: pointer;
  border-bottom: 1px solid #F5F7FA;
  transition: background 0.15s;
}

.notice-item:hover {
  background: #F4FAFA;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot.orange { background: #FF8F00; box-shadow: 0 0 6px rgba(255,143,0,0.5); }
.dot.red { background: #FF4D4F; box-shadow: 0 0 6px rgba(255,77,79,0.5); }
.dot.blue { background: #4A9EF5; box-shadow: 0 0 6px rgba(74,158,245,0.5); }
.dot.purple { background: #722ED1; box-shadow: 0 0 6px rgba(114,46,209,0.5); }
.dot.green { background: #52C41A; box-shadow: 0 0 6px rgba(82,196,26,0.5); }

.notice-text {
  flex: 1;
  font-size: 13px;
  color: #2C3A4B;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-text b {
  color: #FF4D4F;
}

.ev-time {
  color: #999;
  font-size: 12px;
}

.notice-item .arrow {
  color: #B8C4CE;
  font-size: 13px;
}

.user-box {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.2s;
}

.user-box:hover {
  background: #F4FAFA;
}

.user-name {
  color: #2C3A4B;
  font-size: 14px;
}

.page-container {
  margin: 16px;
  padding: 0;
}

.site-footer {
  padding: 10px 16px 16px;
  color: #8c8c8c;
  background: transparent;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.site-footer a {
  color: inherit;
}

.site-footer a:hover {
  color: #4ECDC4;
}
</style>
